import { useEffect, useMemo, useState, type FormEvent } from 'react';
import type { AnnexMode, ContractAnnexRow, ContractPendingSale, ContractCompanyRow, ContractPreviewResponse, ContractTemplateRow, ContractTemplateVersionView } from '@kassa/shared';
import { api, ApiError } from '../api';
import { annexFileUrl, contractFileUrl } from '../api/contracts';
import { amountText, annexBadge, contractBadge } from '../contracts';
import { isoToDmy } from '../dates';
import { DataTable, PageHeader } from '../components/ListPage';
import { ContractPreview } from './ContractPreview';

// Ekran 19 «Müqavilələr» (KT-82, docs/dizayn/Muqavile.dc.html): şirkət öz Word şablonunu yükləyir; proqram mətn yazmır.
// Sol — VÖEN-li alıcılar, sağ — seçilmiş alıcının müqaviləsi, əlavə rejimi və əlavələri. Əlavə №N satışda yaranır.

type Tab = 'contracts' | 'templates' | 'requisites';

const MODES: { value: AnnexMode; label: string; hint: string }[] = [
  { value: 'auto', label: 'Əlavə avtomatik hazırlansın', hint: 'Hər satışda Əlavə №N özü yaranır' },
  { value: 'ask', label: 'Hər dəfə soruşulsun', hint: 'Satışı yazan qərar verir' },
  { value: 'none', label: 'Əlavə hazırlanmasın', hint: 'Yalnız qaimə və çek' },
];

const errText = (e: unknown) => (e instanceof ApiError || e instanceof Error ? e.message : 'Xəta baş verdi');
const today = () => new Date().toISOString().slice(0, 10);

export function Contracts({ onOpenTemplate, onOpenSettings }: { onOpenTemplate: (versionId: string) => void; onOpenSettings: () => void }) {
  const [tab, setTab] = useState<Tab>('contracts');
  const [templates, setTemplates] = useState<ContractTemplateRow[] | null>(null);
  const [tplError, setTplError] = useState<string | null>(null);

  const loadTemplates = () =>
    api
      .contractTemplates()
      .then((t) => {
        setTemplates(t);
        setTplError(null);
      })
      .catch((e: unknown) => setTplError(errText(e)));
  useEffect(() => void loadTemplates(), []);

  return (
    <div className="page">
      <PageHeader
        title="Müqavilələr"
        sub="Əsas müqavilə + hər satışa əlavə. Mətn şirkətin öz Word faylındandır."
        tabs={
          <div className="head-tabs" role="tablist" aria-label="Müqavilə bölmələri">
            {(
              [
                ['contracts', 'Müqavilələr'],
                ['templates', `Yüklənmiş şablonlar (${templates?.length ?? 0})`],
                ['requisites', 'Bizim rekvizitlər'],
              ] as [Tab, string][]
            ).map(([k, label]) => (
              <button key={k} type="button" role="tab" aria-selected={tab === k} className="head-tab" onClick={() => setTab(k)}>
                {label}
              </button>
            ))}
          </div>
        }
      />
      {tab === 'contracts' && <ContractsTab templates={templates ?? []} onGoTemplates={() => setTab('templates')} />}
      {tab === 'templates' && <TemplatesTab templates={templates} error={tplError} onOpen={onOpenTemplate} onUploaded={(v) => onOpenTemplate(v.id)} />}
      {tab === 'requisites' && (
        <section className="card" aria-label="Bizim rekvizitlər">
          <h2>Bizim rekvizitlər</h2>
          <p>Öz VÖEN, bank və direktor məlumatınızı Tənzimləmə → Şirkət bölməsində yazırsınız. Alıcının rekvizitləri müştəri kartındadır. Şirkət seçilən kimi bu məlumatlar müqaviləyə və əlavəyə avtomatik keçir.</p>
          <button type="button" className="btn-secondary" onClick={onOpenSettings}>
            Tənzimləmə → Şirkət
          </button>
        </section>
      )}
    </div>
  );
}

function ContractsTab({ templates, onGoTemplates }: { templates: ContractTemplateRow[]; onGoTemplates: () => void }) {
  const [q, setQ] = useState('');
  const [rows, setRows] = useState<ContractCompanyRow[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [selected, setSelected] = useState<string | null>(null);
  const [creating, setCreating] = useState(false);

  const load = (query: string) =>
    api
      .contractCompanies(query)
      .then((r) => {
        setRows(r);
        setError(null);
      })
      .catch((e: unknown) => setError(errText(e)));
  useEffect(() => {
    const t = window.setTimeout(() => void load(q), 250);
    return () => window.clearTimeout(t);
  }, [q]);

  const company = rows?.find((r) => r.id === selected) ?? null;
  const mains = templates.filter((t) => t.kind === 'main' && t.active);

  return (
    <div className="contract-layout">
      <section className="card contract-list" aria-label="Alıcılar">
        <label className="field">
          <span>Axtarış</span>
          <input type="search" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Şirkət adı və ya VÖEN" />
        </label>
        {error && (
          <p className="bad" role="alert">
            {error}
          </p>
        )}
        {rows === null && !error && <p className="muted">Yüklənir…</p>}
        {rows?.length === 0 && <p className="muted">VÖEN-li alıcı tapılmadı. Müştəri kartında VÖEN yazın.</p>}
        <ul className="contract-companies">
          {rows?.map((r) => (
            <li key={r.id}>
              <button
                type="button"
                className={r.id === selected ? 'contract-company active' : 'contract-company'}
                aria-pressed={r.id === selected}
                onClick={() => {
                  setSelected(r.id);
                  setCreating(false);
                }}
              >
                <span className="contract-company-name">{r.name}</span>
                <span className="muted small mono">{r.voen ?? '—'}</span>
                <span className="small">{r.contract ? `№${r.contract.number}` : 'Müqavilə yoxdur'}</span>
                <span className="muted small">{r.annexCount} əlavə</span>
              </button>
            </li>
          ))}
        </ul>
      </section>
      <div className="contract-detail">
        {!company && <p className="muted card">Siyahıdan alıcını seçin.</p>}
        {company && !creating && <CompanyPanel key={company.id} company={company} onCreate={() => setCreating(true)} onChanged={() => void load(q)} hasTemplates={mains.length > 0} onGoTemplates={onGoTemplates} />}
        {company && creating && (
          <NewContract
            company={company}
            templates={mains}
            onCancel={() => setCreating(false)}
            onDone={() => {
              setCreating(false);
              void load(q);
            }}
          />
        )}
      </div>
    </div>
  );
}

function CompanyPanel({ company, onCreate, onChanged, hasTemplates, onGoTemplates }: { company: ContractCompanyRow; onCreate: () => void; onChanged: () => void; hasTemplates: boolean; onGoTemplates: () => void }) {
  const [annexes, setAnnexes] = useState<ContractAnnexRow[]>([]);
  const [pending, setPending] = useState<ContractPendingSale[]>([]);
  const [loading, setLoading] = useState(false);
  const [busy, setBusy] = useState(false);
  const [seq, setSeq] = useState(0);
  const [error, setError] = useState<string | null>(null);
  const [mode, setMode] = useState<AnnexMode>(company.annexMode);
  const k = company.contract;

  useEffect(() => {
    if (!k) return;
    setLoading(true);
    Promise.all([api.contractAnnexes(k.id), api.contractPendingSales(k.id)])
      .then(([a, p]) => {
        setAnnexes(a);
        setPending(p);
      })
      .catch((e: unknown) => setError(errText(e)))
      .finally(() => setLoading(false));
  }, [k, seq]);

  const act = async (f: () => Promise<unknown>) => {
    setBusy(true);
    setError(null);
    try {
      await f();
      setSeq((n) => n + 1);
      onChanged();
    } catch (e) {
      setError(errText(e));
    } finally {
      setBusy(false);
    }
  };

  const changeMode = (m: AnnexMode) => {
    const prev = mode;
    setMode(m);
    api
      .contractAnnexMode(company.id, m)
      .then(onChanged)
      .catch((e: unknown) => {
        setMode(prev);
        setError(errText(e));
      });
  };

  if (!k) {
    return (
      <section className="card" aria-label={`${company.name} — müqavilə`}>
        <h2>{company.name}</h2>
        <p className="muted">Müqavilə yoxdur.</p>
        {hasTemplates ? (
          <button type="button" className="btn-primary" onClick={onCreate}>
            Öz şablonumla müqavilə yarat
          </button>
        ) : (
          <>
            <p>Əvvəl öz Word şablonunuzu (.docx) yükləyin.</p>
            <button type="button" className="btn-secondary" onClick={onGoTemplates}>
              Şablon yüklə
            </button>
          </>
        )}
      </section>
    );
  }
  const badge = contractBadge(k);
  const next = k.lastAnnexNo + 1;
  return (
    <>
      <section className="card" aria-label={`${company.name} — əsas müqavilə`}>
        <div className="card-head">
          <h2>Əsas müqavilə</h2>
          <span className={`badge ${badge.cls}`}>{badge.label}</span>
        </div>
        <p className="contract-no mono">
          №{k.number} · {isoToDmy(k.signedOn)}
        </p>
        <p className="muted small">
          {company.name} · {k.templateName}, versiya {k.versionNo}
        </p>
        <div className="form-actions">
          <a className="btn-secondary" href={contractFileUrl(k.id)} download>
            Word
          </a>
          <button type="button" className="btn-secondary" onClick={onCreate}>
            Yeni müqavilə
          </button>
        </div>
      </section>
      <section className="card" aria-label="Əlavə rejimi">
        <h2>Bu şirkətə satış yazılanda</h2>
        <div className="contract-modes" role="radiogroup" aria-label="Əlavə rejimi">
          {MODES.map((m) => (
            <label key={m.value} className={mode === m.value ? 'contract-mode active' : 'contract-mode'}>
              <input type="radio" name="annex-mode" value={m.value} checked={mode === m.value} onChange={() => changeMode(m.value)} />
              <span>
                <strong>{m.label}</strong>
                <span className="muted small"> — {m.hint}</span>
              </span>
            </label>
          ))}
        </div>
        {error && (
          <p className="bad" role="alert">
            {error}
          </p>
        )}
      </section>
      {pending.length > 0 && (
        <section className="card" aria-label="Əlavəsiz satışlar">
          <h2>Əlavəsiz satışlar</h2>
          <ul className="contract-pending">
            {pending.map((s) => (
              <li key={s.docId}>
                <span>
                  {s.docNumber} · {isoToDmy(s.docDate.slice(0, 10))} · <span className="mono">{amountText(s.total)} ₼</span>
                </span>
                <button type="button" className="btn-primary" disabled={busy} onClick={() => void act(() => api.contractAnnexCreate(k.id, s.docId))}>
                  Əlavə №{next} hazırla
                </button>
              </li>
            ))}
          </ul>
        </section>
      )}
      <section className="card" aria-label="Əlavələr">
        <h2>Əlavələr</h2>
        <DataTable
          label="Əlavələr"
          loading={loading}
          error={null}
          rows={annexes}
          rowKey={(a) => a.id}
          empty={<p className="muted">Bu şirkətə əlavəli satış yoxdur. Növbəti əlavə №{next} olacaq.</p>}
          columns={[
            { key: 'n', label: 'Əlavə', mono: true, render: (a) => `№${a.annexNo}` },
            { key: 'd', label: 'Tarix', render: (a) => isoToDmy(a.annexDate) },
            { key: 'doc', label: 'Sənəd', minor: true, render: (a) => a.docNumber },
            { key: 's', label: 'Məbləğ, ₼', align: 'right', mono: true, render: (a) => amountText(a.amount) },
            {
              key: 'st',
              label: 'Status',
              render: (a) => {
                const b = annexBadge(a.status);
                return <span className={`badge ${b.cls}`}>{b.label}</span>;
              },
            },
            {
              key: 'act',
              label: 'Fayl',
              render: (a) => (
                <span className="contract-annex-actions">
                  <a href={annexFileUrl(a.id)} download>
                    Word
                  </a>
                  {a.status !== 'cancelled' && (
                    <button
                      type="button"
                      className="link-btn"
                      disabled={busy}
                      onClick={() => {
                        if (window.confirm(`Əlavə №${a.annexNo} ləğv edilsin? Nömrə təkrar verilmir.`)) void act(() => api.contractAnnexCancel(a.id));
                      }}
                    >
                      Ləğv et
                    </button>
                  )}
                </span>
              ),
            },
          ]}
        />
      </section>
    </>
  );
}

function NewContract({ company, templates, onCancel, onDone }: { company: ContractCompanyRow; templates: ContractTemplateRow[]; onCancel: () => void; onDone: () => void }) {
  const [templateId, setTemplateId] = useState(templates[0]?.id ?? '');
  const [version, setVersion] = useState<ContractTemplateVersionView | null>(null);
  const [number, setNumber] = useState('');
  const [signedOn, setSignedOn] = useState(today());
  const [validUntil, setValidUntil] = useState('');
  const [paymentTerm, setPaymentTerm] = useState('');
  const [manual, setManual] = useState<Record<string, string>>({});
  const [preview, setPreview] = useState<ContractPreviewResponse | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const tpl = templates.find((t) => t.id === templateId) ?? null;
  useEffect(() => {
    if (!tpl) return;
    api
      .contractVersion(tpl.latestVersionId)
      .then(setVersion)
      .catch((e: unknown) => setError(errText(e)));
  }, [tpl]);

  const input = useMemo(
    () =>
      version && {
        templateVersionId: version.id,
        counterpartyId: company.id,
        legalEntityId: null,
        number: number.trim(),
        signedOn,
        validUntil: validUntil || null,
        paymentTerm: paymentTerm.trim() || null,
        manualValues: manual,
      },
    [version, company.id, number, signedOn, validUntil, paymentTerm, manual],
  );

  // Baxış dəyərlər dəyişdikcə yenilənir (yarım saniyə gözləyib)
  useEffect(() => {
    if (!input) return;
    const t = window.setTimeout(() => {
      api
        .contractPreview(input)
        .then((p) => {
          setPreview(p);
          setError(null);
        })
        .catch((e: unknown) => setError(errText(e)));
    }, 400);
    return () => window.clearTimeout(t);
  }, [input]);

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    if (!input) return;
    setBusy(true);
    setError(null);
    try {
      await api.contractCreate(input);
      onDone();
    } catch (err) {
      setError(errText(err));
    } finally {
      setBusy(false);
    }
  };

  const manualFields = version?.fields.filter((f) => f.source === 'manual') ?? [];
  const missing = preview?.missing ?? [];
  return (
    <section className="card" aria-label={`Yeni müqavilə — ${company.name}`}>
      <h2>Yeni müqavilə · {company.name}</h2>
      <form className="contract-form" onSubmit={(e) => void submit(e)}>
        <label className="field">
          <span>Şablon</span>
          <select value={templateId} onChange={(e) => setTemplateId(e.target.value)}>
            {templates.map((t) => (
              <option key={t.id} value={t.id}>
                {t.name} · v{t.latestVersionNo}
              </option>
            ))}
          </select>
        </label>
        <label className="field">
          <span>Nömrə</span>
          <input value={number} onChange={(e) => setNumber(e.target.value)} placeholder={preview?.suggestedNumber ? `${preview.suggestedNumber} (avtomatik)` : ''} maxLength={50} />
        </label>
        <label className="field">
          <span>Tarix</span>
          <input type="date" value={signedOn} onChange={(e) => setSignedOn(e.target.value)} required />
        </label>
        <label className="field">
          <span>Qüvvədədir (tarixədək)</span>
          <input type="date" value={validUntil} onChange={(e) => setValidUntil(e.target.value)} />
        </label>
        <label className="field">
          <span>Ödəniş müddəti</span>
          <input value={paymentTerm} onChange={(e) => setPaymentTerm(e.target.value)} placeholder="məs. 30 gün" maxLength={200} />
        </label>
        {manualFields.map((f) => (
          <label key={f.tag} className="field">
            <span>#{f.tag}# (əl ilə)</span>
            <input value={manual[f.tag] ?? ''} onChange={(e) => setManual((m) => ({ ...m, [f.tag]: e.target.value }))} />
          </label>
        ))}
        {missing.length > 0 && (
          <p className="bad" role="status">
            Boş sahələr: {missing.map((t) => `#${t}#`).join(', ')} — müqavilə bağlanmır.
          </p>
        )}
        {error && (
          <p className="bad" role="alert">
            {error}
          </p>
        )}
        <div className="form-actions">
          <button type="submit" className="btn-primary" disabled={busy || !input || missing.length > 0}>
            Müqaviləni bağla
          </button>
          <button type="button" className="btn-secondary" onClick={onCancel}>
            Ləğv et
          </button>
        </div>
      </form>
      {preview && <ContractPreview paragraphs={preview.preview} mode="filled" label="Doldurulmuş baxış" />}
    </section>
  );
}

function TemplatesTab({ templates, error, onOpen, onUploaded }: { templates: ContractTemplateRow[] | null; error: string | null; onOpen: (versionId: string) => void; onUploaded: (v: ContractTemplateVersionView) => void }) {
  const [file, setFile] = useState<File | null>(null);
  const [name, setName] = useState('');
  const [kind, setKind] = useState<'main' | 'annex'>('main');
  const [busy, setBusy] = useState(false);
  const [upError, setUpError] = useState<string | null>(null);

  const upload = async (e: FormEvent) => {
    e.preventDefault();
    if (!file) return;
    setBusy(true);
    setUpError(null);
    try {
      onUploaded(await api.contractTemplateCreate(file, name.trim() || file.name.replace(/\.docx$/i, ''), kind));
    } catch (err) {
      setUpError(errText(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <>
      <section className="card" aria-label="Öz şablonunu yüklə">
        <h2>Müqaviləni şirkət özü yükləyir</h2>
        <p className="muted">
          Proqram müqavilə mətni yazmır. Öz Word faylınızı (.docx) yükləyin; dəyişən yerləri özünüz adlandırın: şirkət adının yerinə #şirkətadı#, VÖEN-in yerinə #vöen#. Qalan
          mətn olduğu kimi qalır.
        </p>
        <form className="contract-form" onSubmit={(e) => void upload(e)}>
          <label className="field">
            <span>Word faylı (.docx)</span>
            <input
              type="file"
              accept=".docx,application/vnd.openxmlformats-officedocument.wordprocessingml.document"
              onChange={(e) => {
                const f = e.target.files?.[0] ?? null;
                setFile(f);
                if (f && !name) setName(f.name.replace(/\.docx$/i, ''));
              }}
            />
          </label>
          <label className="field">
            <span>Ad</span>
            <input value={name} onChange={(e) => setName(e.target.value)} maxLength={200} />
          </label>
          <label className="field">
            <span>Növ</span>
            <select value={kind} onChange={(e) => setKind(e.target.value as 'main' | 'annex')}>
              <option value="main">Əsas müqavilə</option>
              <option value="annex">Əlavə (spesifikasiya)</option>
            </select>
          </label>
          {upError && (
            <p className="bad" role="alert">
              {upError}
            </p>
          )}
          <div className="form-actions">
            <button type="submit" className="btn-primary" disabled={busy || !file}>
              Öz şablonunu yüklə
            </button>
          </div>
        </form>
      </section>
      <section className="card" aria-label="Yüklənmiş şablonlar">
        <h2>Yüklənmiş şablonlar</h2>
        <DataTable
          label="Yüklənmiş şablonlar"
          loading={templates === null}
          error={error}
          rows={templates ?? []}
          rowKey={(t) => t.id}
          onEdit={(t) => onOpen(t.latestVersionId)}
          editLabel="Şablonu aç, sahələri dəyiş"
          empty={<p className="muted">Hələ şablon yoxdur.</p>}
          columns={[
            { key: 'name', label: 'Ad', render: (t) => t.name },
            { key: 'kind', label: 'Növ', render: (t) => (t.kind === 'main' ? 'Əsas müqavilə' : 'Əlavə') },
            { key: 'v', label: 'Versiya', align: 'right', mono: true, render: (t) => `v${t.latestVersionNo}` },
            { key: 'f', label: 'Sahə', align: 'right', minor: true, render: (t) => t.fieldCount },
            { key: 'c', label: 'Müqavilə', align: 'right', minor: true, render: (t) => t.contractCount },
          ]}
        />
      </section>
    </>
  );
}
